import {Component, EventEmitter, OnInit, Output} from '@angular/core';
declare let L: any;
import { fullScreenMap } from './mapFullScreen';
import 'leaflet.markercluster';
import {CaseService} from '../../../pages/case/services/case.services';
import {textColorPicker} from '../../common/common';
import {AppMessageService} from '../../services/app-message/app-message.service';
import {AppAlertService} from '../../services/app-alert/app-alert.service';
import {TranslateService} from '@ngx-translate/core';
import {ParameterService} from '../../services/parameter/parameter.service';

interface Vehicle {
  id: number;
  vehicleNo: string;
  licensePlate: string;
  mobileStatusVehicle?: {
    id: number;
    dot: {
      id: number;
      dotName: string;
    };
    vehicleStatus: {
      id: number;
      name: string;
      color: string;
    };
  } | null;
  vehicleTracking?: {
    id: number;
    gpsData: {
      latitude: number;
      longitude: number;
      gpsTime: string;
    };
    distance?: number | null;
  } | null;
  marker?: any;
}

@Component({
  selector: 'app-case-assign-vehicle',
  templateUrl: './case-assign-vehicle.component.html',
  styleUrl: './case-assign-vehicle.component.scss',
})
export class CaseAssignVehicleComponent implements OnInit {
  isVisible: boolean = false;
  searchTerm = '';
  showOnlyAvailable = true;
  isFullscreen = false;
  private map!: L.Map;
  private markerClusterGroup!:any;
  selectedVehicle: Vehicle | null = null;
  chosenVehicle: Vehicle | null = null;
  caseLocation: { latitude: number | null; longitude: number | null } = {
    latitude: null,
    longitude: null,
  };

  vehicles: Vehicle[] = [];
  filteredVehicles: Vehicle[] = [];
  loading: boolean = false;
  caseId: number | null;
  availableStatusId: number | null;

  @Output() changeOn = new EventEmitter<any>();

  constructor(
    private readonly caseService: CaseService,
    private readonly messageService: AppMessageService,
    private readonly alertService: AppAlertService,
    private readonly translateService: TranslateService,
    private readonly parameterService: ParameterService
  ) {
  }

  ngOnInit(): void {
    this.parameterService
      .getParameterValueByKeyname('case-available-status-id')
      .subscribe({
        next: (res) => {
          this.availableStatusId =
            res?.value && !Number.isNaN(Number(res.value)) ? Number(res.value) : null;
        },
      });
  }

  closeMap() {
    this.selectedVehicle = null;
    this.caseId = null;
    this.filteredVehicles = [];
    this.vehicles = [];
    if (this.map) {
      this.map.remove();
    }
  }

  closeDialog() {
    this.isVisible = false;
  }

  private initMap() {
    this.map = L.map('map', {attributionControl: true}).setView(
      [
        this.caseLocation?.latitude ?? 41.0152,
        this.caseLocation?.longitude ?? 28.5768,
      ],
      this.caseLocation?.latitude && this.caseLocation?.longitude  ? 12:9,
    );
    this.map.attributionControl.setPrefix('İDEA');

    let layers = {
      OSM: L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 30,
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      }),
      CartoDB_Positron: L.tileLayer(
        'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
        {
          attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
          subdomains: 'abcd',
          maxZoom: 30,
        }
      ),

      Streets: L.tileLayer(
        'http://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}',
        {
          maxZoom: 30,
          subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
        }
      ),
      Hybrid: L.tileLayer(
        'http://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}',
        {
          maxZoom: 30,
          subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
        }
      ),
      Satellite: L.tileLayer(
        'http://{s}.google.com/vt/lyrs=s&x={x}&y={y}&z={z}',
        {
          maxZoom: 30,
          subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
        }
      ),
      Terrain: L.tileLayer(
        'http://{s}.google.com/vt/lyrs=p&x={x}&y={y}&z={z}',
        {
          maxZoom: 30,
          subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
        }
      ),
    };

    layers.Streets.addTo(this.map);
    L.control.layers(layers).addTo(this.map);

    fullScreenMap().addTo(this.map);

    this.markerClusterGroup = L.markerClusterGroup({
      showCoverageOnHover: false,
      spiderfyOnMaxZoom: true,
      zoomToBoundsOnClick: true,
      maxClusterRadius: 40,
      // iconCreateFunction: (cluster) => {
      //   const count = cluster.getChildCount();
      //   return L.divIcon({
      //     html: `<div class="marker-cluster">${count}</div>`,
      //     className: 'custom-cluster-icon',
      //     iconSize: L.point(36, 36)
      //   });
      // }
    });
    for(const vehicle of this.filteredVehicles){
      const lat = vehicle?.vehicleTracking?.gpsData?.latitude ?? null;
      const lng = vehicle?.vehicleTracking?.gpsData?.longitude ?? null;

      if (lat && lng && vehicle.mobileStatusVehicle?.vehicleStatus) {
        const marker = this.createCustomMarker(vehicle, L.latLng(lat, lng));
        this.markerClusterGroup.addLayer(marker);
        vehicle.marker = marker;
      }
    }

    this.map.addLayer(this.markerClusterGroup);

    if (this.caseLocation?.latitude && this.caseLocation?.longitude) {
      const incidentMarker = this.createIncidentMarker({
        latitude: this.caseLocation?.latitude,
        longitude: this.caseLocation?.longitude,
      });
      incidentMarker.addTo(this.map);
    }
  }

  open(caseId: number, caseCoord: { latitude: number; longitude: number }) {
    this.isVisible = true;
    this.loading = true;
    this.caseId = caseId;
    this.caseLocation = {
      latitude: caseCoord?.latitude || null,
      longitude: caseCoord?.longitude || null,
    };

    this.caseService.getVehiclesForCase(caseId).subscribe({
      next: (res) => {
        if (res.success) {
          this.vehicles = res.vehiclesList ?? [];
          this.filteredVehicles = res.vehiclesList ?? [];
          this.loading = false;
          setTimeout(() => {
            this.initMap();
            this.filterArray()

          }, 300);
        }
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  private createIncidentMarker(incident: {
    latitude: number;
    longitude: number;
  }) {
    const iconHtml = `
      <div class="incident-marker">
        <div class="incident-icon">🚨</div>
        <div class="incident-pulse"></div>
      </div>
    `;
    const icon = L.divIcon({
      className: 'incident-marker-container',
      html: iconHtml,
      iconSize: [40, 40],
      iconAnchor: [20, 20],
    });

    const marker = L.marker([incident.latitude, incident.longitude], {icon});
    marker.bindTooltip(`Olay Yeri`, {
      permanent: true,
      direction: 'bottom',
      className: 'vehicle-tooltip',
      offset: [0, 10],
    });

    marker.bindPopup(`
      <div class="incident-popup">
        <h3>Olay Yeri</h3>
      </div>
    `);
    // <p>Acil Vaka - Hasta Müdahale Bekliyor </p>

    return marker;
  }

  zoomToVehicle(event: any) {
    const vehicle = event?.data;
    this.chosenVehicle = vehicle;
    if (
      !vehicle?.vehicleTracking?.gpsData?.latitude ||
      !vehicle?.vehicleTracking?.gpsData?.longitude ||
      !vehicle.marker
    ) {
      this.zoomToCase(event, true);
    } else {
      this.selectedVehicle = vehicle;
      for(const v of this.filteredVehicles){
        if (v.marker) {
          v.marker.setStyle({
            weight: 2,
            radius: 8,
          });
          v.marker.closePopup();
        }
      }

      vehicle.marker.setStyle({
        weight: 3,
        radius: 12,
      });

      vehicle.marker.openPopup();
      this.map.setView(
        [
          vehicle.vehicleTracking.gpsData.latitude,
          vehicle.vehicleTracking.gpsData.longitude,
        ],
        14
      );
    }
  }

  zoomToCase(event: any, selectable: boolean) {
    const vehicle = event?.data;
    if (!selectable) {
      this.chosenVehicle = null;
    }
    this.selectedVehicle = null;
    vehicle?.marker?.closePopup();
    this.map.setView(
      [this.caseLocation?.latitude ?? 41.0152, this.caseLocation?.longitude ?? 28.5768],
      9
    );
  }

  toggleFullscreen() {
    this.isFullscreen = !this.isFullscreen;
    setTimeout(() => {
      this.map.invalidateSize();
    }, 300);
  }

  // changeMapLayer(url: string) {
  //   if (this.currentTileLayer) {
  //     this.map.removeLayer(this.currentTileLayer);
  //   }

  private createCustomMarker(
    vehicle: Vehicle,
    latlng: L.LatLng
  ): L.CircleMarker {
    const markerColor =
      vehicle?.mobileStatusVehicle?.vehicleStatus?.color ?? 'black';

    // Marker (Daire)
    const marker = L.circleMarker(latlng, {
      radius: 12,
      fillColor: markerColor,
      color: '#fff',
      weight: 3,
      opacity: 1,
      fillOpacity: 0.9,
    }).addTo(this.map);

    marker.bindTooltip(`${vehicle?.id} - ${vehicle?.licensePlate}`, {
      permanent: true,
      direction: 'bottom',
      className: 'vehicle-tooltip',
      offset: [0, 10],
    });

    // Popup (detay bilgi)
    marker.bindPopup(`
      <b>${vehicle?.id}</b><br>
      ${vehicle?.licensePlate}<br>
      <span style="color: ${markerColor}">${
      vehicle?.mobileStatusVehicle?.vehicleStatus?.name
    }</span><br>
      ${vehicle?.mobileStatusVehicle?.dot?.dotName ?? ''}
    `);

    return marker;
  }

  textColorPicker(color: string) {
    if (!color) {
      return;
    }
    return textColorPicker(color);
  }

  assignVehicle() {
    if (!this.chosenVehicle) {
      this.messageService.errorToast(
        this.translateService.instant('common.error'),
        this.translateService.instant('case.assign-vehicle.no-selected-vehicle')
      );
      return;
    }

    if (!this.caseId) {
      this.messageService.errorToast(
        this.translateService.instant('common.error'),
        this.translateService.instant('case.assign-vehicle.no-case-id')
      );
      return;
    }

    if (
      !this.chosenVehicle?.mobileStatusVehicle?.vehicleStatus.id?.toString()
    ) {
      this.messageService.errorToast(
        this.translateService.instant('common.error'),
        this.translateService.instant('case.assign-vehicle.undefined-status')
      );

      return;
    }

    //araç müsait id sine eşit değilse ya da sistem parametrelerinde availablestatusId yoksa
    //araç müsait değil uyarısı bastır
    if (
      this.chosenVehicle.mobileStatusVehicle.vehicleStatus.id.toString() !==
      this.availableStatusId?.toString()
    ) {
      this.messageService.errorToast(
        this.translateService.instant('common.error'),
        this.translateService.instant('case.assign-vehicle.not-available')
      );
      return;
    }

    this.alertService.sweetConfirm({
      header: this.translateService.instant('case.assign-vehicle.sure-header'),
      message:
        this.selectedVehicle?.licensePlate +
        ' ' +
        this.translateService.instant('case.assign-vehicle.sure-text'),
      showCloseButton: true,
      accept: () => this.save(),
    });
  }

  filterArray() {
    const term = this.searchTerm?.toLowerCase()?.trim();
    this.filteredVehicles = [...this.vehicles];

    if (term) {
      this.filteredVehicles = this.filteredVehicles.filter((vehicle) => {
        const vehicleNo = vehicle?.vehicleNo?.toLowerCase() ?? '';
        const licensePlate = vehicle?.licensePlate?.toLowerCase() ?? ''
         const point = vehicle?.mobileStatusVehicle?.dot?.dotName?.toLowerCase() ?? '';
        return vehicleNo.includes(term) || licensePlate.includes(term) || point.includes(term);
      });
    }

    if (this.showOnlyAvailable) {
      this.filteredVehicles = this.filteredVehicles
        .filter(
          (e) =>
            e.mobileStatusVehicle?.vehicleStatus?.id?.toString() ===
            this.availableStatusId?.toString()
        )
        .map((f) => f);
    }
    this.showAvailable();
  }

  showAvailable() {
    // 1. Var olan marker'ları temizle
    this.markerClusterGroup.clearLayers();

    // 2. Yeni marker'ları ekle
    for(const vehicle of this.filteredVehicles){
      const lat = vehicle?.vehicleTracking?.gpsData?.latitude ?? null;
      const lng = vehicle?.vehicleTracking?.gpsData?.longitude ?? null;

      if (lat && lng) {
        const marker = this.createCustomMarker(vehicle, L.latLng(lat, lng));
        this.markerClusterGroup.addLayer(marker);
        vehicle.marker = marker;
      }
    }
  }

  save() {
    this.caseService
      .assignVehicle(this.caseId!, this.chosenVehicle?.id!, true)
      .subscribe({
        next: (res) => {
          if (res.success) {
            this.messageService.successToast(
              this.translateService.instant('common.success'),
              this.translateService.instant(
                'case.assign-vehicle.assigned-success'
              )
            );
            this.closeDialog();
            this.changeOn.emit();
          }
        },
      });
  }
}
